// src/lib/ownerWeek.js
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. OwnerSnapshot measures one manager action and there are
//               now four.
//
// IS THE PROCESS BEING RUN. THE ONE QUESTION AN OWNER CANNOT ANSWER ANY OTHER
// WAY.
//
// `OwnerSnapshot` was written on 2026-08-30 and its argument is right: sign
// offs are the number that measures the MANAGER rather than the floor, because
// a sign off is the one action only a manager can take and it requires them to
// stand next to somebody and watch.
//
// It was also the only manager action this product could see at the time. Since
// then the morning meeting, the one on one and the daily audit have all become
// things that leave a trace, so the owner's picture can be four wide instead of
// one.
//
// WHY THIS MATTERS COMMERCIALLY, from the 2026-09-24 research pass.
//
// The trade's own read on why dealership training fails in 2026: "consistent
// execution will depend on manager-led training and leadership, not tools
// alone", and "the assumption that new people can still learn by watching
// experienced people is broken, and almost no one has replaced it with a real
// system."
//
// A DMS can tell an owner what sold. Nothing can tell them whether the morning
// meeting happened. That is the gap this screen stands in, and it is the thing
// they are actually buying.
//
// REDUCED TIME OUT OF ROLE is the adoption criterion the same research names,
// so this has to be readable in half a minute. Four rows, a number on each,
// nothing to click to understand it.
//
// SAID AS FACT, NEVER AS ACCUSATION. Same rule OwnerSnapshot set: "nothing has
// been signed off in the last 7 days" is a true sentence an owner can take into
// a conversation. "Your manager is not doing their job" is a conclusion that is
// not ours to draw and would be wrong the week somebody was on holiday.

import { dayKey, addDays } from './localDate.js'

export const WINDOW_DAYS = 7

// Working days in the window. A morning meeting on a Sunday is not a miss, and
// a screen that counts it as one teaches an owner to ignore the screen.
export function workingDays(days) {
  return (days || []).filter((d) => {
    const [y, m, dd] = d.split('-').map(Number)
    const wd = new Date(y, m - 1, dd).getDay()
    return wd !== 0
  })
}

export function lastDays(n, from = dayKey()) {
  const out = []
  for (let i = 0; i < n; i += 1) out.push(addDays(from, -i))
  return out
}

// The four things a manager does that leave a trace.
//
// `of` is the honest denominator in each case, and they are different on
// purpose: meetings and the audit are per working day, one on ones are per
// person on the floor, and sign offs have no denominator at all because there
// is no right number of them in a week.
export function disciplineRows({ days, meetings, oneOnOnes, audits, signoffs, floorSize }) {
  const work = workingDays(days)
  const workSet = new Set(work)

  const held = new Set(
    (meetings || []).filter((m) => m.held_at && workSet.has(m.meeting_on)).map((m) => m.meeting_on)
  )
  const sawSomebody = new Set(
    (oneOnOnes || []).filter((o) => days.includes(o.held_on)).map((o) => o.salesperson_id)
  )
  const auditDays = new Set(
    (audits || []).filter((a) => workSet.has(a.on_date) && (a.items || []).length > 0).map((a) => a.on_date)
  )
  const decided = (signoffs || []).filter((s) => s.decided_at && days.includes(String(s.decided_at).slice(0, 10)))

  return [
    {
      key: 'morning',
      label: 'Morning meetings held',
      count: held.size,
      of: work.length,
      unit: 'working days',
    },
    {
      key: 'oneonone',
      label: 'People who had a one on one',
      count: sawSomebody.size,
      of: floorSize || 0,
      unit: 'on the floor',
    },
    {
      key: 'audit',
      label: 'End of day inspections',
      count: auditDays.size,
      of: work.length,
      unit: 'working days',
    },
    {
      key: 'signoff',
      label: 'Sign offs decided',
      count: decided.length,
      of: null,
      unit: 'this week',
    },
  ]
}

// One sentence at the top, computed rather than written so it is never wrong
// about its own numbers.
//
// It leads with the thing an owner can act on and it never grades. A store
// where nothing at all happened gets told that plainly, because a screen that
// hunts for a positive spin on a dead week is a screen nobody believes the week
// something is genuinely wrong.
export function ownerHeadline(rows, floorSize) {
  if (!floorSize) return 'Nobody is on this floor yet.'
  const by = Object.fromEntries(rows.map((r) => [r.key, r]))
  const nothing = rows.every((r) => r.count === 0)
  if (nothing) return 'Nothing was recorded on this floor in the last seven days.'

  const missed = by.morning.of - by.morning.count
  if (missed >= 3) {
    return `The morning meeting ran on ${by.morning.count} of ${by.morning.of} working days.`
  }
  if (by.oneonone.count === 0) {
    return 'Nobody on the floor had a one on one this week.'
  }
  if (by.signoff.count === 0) {
    return 'Nothing was signed off this week.'
  }
  if (by.oneonone.count < floorSize) {
    return `${by.oneonone.count} of ${floorSize} had a one on one, and the morning meeting ran on ${by.morning.count} of ${by.morning.of} days.`
  }
  return 'Every discipline ran this week.'
}

// Colour is a judgement, so it is applied narrowly: green only when the thing
// genuinely happened throughout, amber when it partly did, and nothing at all
// when there is no denominator to be short against.
export function toneFor(row) {
  if (row.of == null) return row.count > 0 ? 'good' : 'quiet'
  if (row.of === 0) return 'quiet'
  if (row.count >= row.of) return 'good'
  if (row.count === 0) return 'none'
  return 'partial'
}
